import { ClickAwayListener } from "@mui/base";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import InputAdornment from "@mui/material/InputAdornment";
import InputBase from "@mui/material/InputBase";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { MdsAddBox, MdsChips, MdsKeyboardArrowDown, MdsKeyboardArrowUp } from "@sinch/icons";
import { isEmpty, last, uniq } from "ramda";
import React, { useCallback, useMemo } from "react";
import { FieldValues, FormProvider, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControlWrapper, MdsIcon, useMuiController } from "@sinch/components";
import { NationalHolidaysSettings } from "@sinch/components/application/NationalHolidaysSettings/NationalHolidaysSettings";
import { ConditionSelectProps } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import {
  NationalHolidayConditionFormFields,
  nationalHolidayConditionSchema,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/nationalHolidayFormSchema";
import { useConditionValuePopover } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/useConditionValuePopover";
import { timeRange } from "@sinch/components/application/TimeIntervalSettings/formSchema";
import { StringTimeInput } from "@sinch/components/application/TimeIntervalSettings/StringTimeInput";
import { Pill } from "@sinch/components/Pill/Pill";
import { PopoverProvider } from "@sinch/components/providers/PopoverProvider";

/**
 * Height of the scrolling holiday list. Fits roughly six holidays, enough to read the list without
 * letting a country with 25 of them stretch the popover.
 */
const HOLIDAY_LIST_MAX_HEIGHT = 168;

/**
 * Value input of the "Attendance beginning on national holiday" condition: pick the country whose
 * holidays count, opt individual holidays out, and narrow the match to time windows within the day.
 */
export const ConditionValueInputNationalHoliday = ({
  onChange,
  value,
  onOpenChange,
  onBlur,
  readOnly,
}: ConditionSelectProps<NationalHolidayConditionFormFields>) => {
  const { safeLanguageCode } = useSettings();
  const externalValues = useMemo(
    () => ({
      ...nationalHolidayConditionSchema.getDefault(),
      ...(value ?? {}),
    }),
    [value]
  );

  const form = useMuiForm<NationalHolidayConditionFormFields>({
    schema: nationalHolidayConditionSchema,
    formOptions: {
      values: externalValues,
      resetOptions: { keepDefaultValues: true },
      defaultValues: externalValues,
    },
  });
  const { open, anchorEl, setAnchorEl, popoverRef, paperRef, handleToggleOpen, handleClickAway } =
    useConditionValuePopover(onBlur);

  React.useEffect(() => {
    const subscription = form.watch((formData) => {
      try {
        onChange(nationalHolidayConditionSchema.validateSync(formData));
      } catch (error) {
        console.error(error);
        form.trigger();
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, form.handleSubmit, form, onChange]);

  const country = value?.country ?? null;
  const countryLabel =
    country === null ? "" : (new Intl.DisplayNames([safeLanguageCode], { type: "region" }).of(country) ?? country);

  return (
    <Box sx={{ position: "relative", width: "100%", containerType: "inline-size" }}>
      <div ref={setAnchorEl}>
        <FormControlWrapper>
          <InputBase
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  cursor: "pointer",
                  marginLeft: 0,
                  marginRight: -1,
                }}
              >
                <MdsIcon fontSize="small" icon={open ? MdsKeyboardArrowUp : MdsKeyboardArrowDown} />
              </InputAdornment>
            }
            fullWidth
            inputProps={{
              "data-cy": "ConditionInput-valueInput-nationalHoliday",
              readOnly: true,
            }}
            onBlur={(e) => {
              onOpenChange(false);
              onBlur?.(e);
            }}
            onClick={(e) => {
              if (readOnly) {
                return;
              }
              handleToggleOpen(e);
            }}
            onFocus={() => {
              if (readOnly) {
                return;
              }
              onOpenChange(true);
            }}
            readOnly
            sx={[
              { px: 1 },
              value === null
                ? {
                    backgroundColor: "transparent",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: ({ palette }) => palette.grey[500],
                    },
                  }
                : {},
            ]}
            value={countryLabel}
          />
        </FormControlWrapper>
      </div>
      <PopoverProvider>
        <Popover
          action={popoverRef}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disablePortal={false}
          open={open}
          slotProps={{
            paper: {
              ref: paperRef,
              elevation: 1,
              sx: {
                minWidth: "250px",
                maxWidth: "min(650px, 100vh)",
                overflow: "auto",
                zIndex: 1,
                paddingBottom: 2,
              },
            },
          }}
          sx={{ minWidth: "min(650px, 100vh)", width: "100%", zIndex: 1000 }}
        >
          <FormProvider {...form}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <NationalHolidaysSettings listMaxHeight={HOLIDAY_LIST_MAX_HEIGHT} path="" showWeekends={false} />
                <HolidayTimes />
              </Stack>
            </ClickAwayListener>
          </FormProvider>
        </Popover>
      </PopoverProvider>
    </Box>
  );
};

/**
 * Time windows within the holiday. An empty list means the whole day, the same convention the
 * weekday rows of the plain beginning condition use.
 */
const HolidayTimes = () => {
  const { t } = useTranslation();
  const {
    field: { error, errorMessage },
  } = useMuiController<NationalHolidayConditionFormFields, "times">({ name: "times" });
  const { fields, append, remove } = useFieldArray<FieldValues>({ name: "times" });

  return (
    <Stack spacing={1}>
      <FormLabel>{t("RateUnit.holidayTimes")}</FormLabel>
      {isEmpty(fields) && (
        <Stack alignItems="center" direction="row" gap={0.5}>
          <Pill
            label={<Typography>{t("RateUnit.entireDay")}</Typography>}
            sx={{ minHeight: "32px", width: "100%" }}
            variant="outlined"
          />
          <AddTimeButton onClick={() => append(timeRange.getDefault())} />
        </Stack>
      )}
      {fields.map((field, index) => (
        <Stack key={field.id} alignItems="center" direction="row" gap={0.5}>
          <TimeRangeItem index={index} />
          <Button
            color="error"
            onClick={() => remove(index)}
            size="medium"
            sx={{
              minWidth: "auto",
              maxHeight: "32px",
              px: 0,
              color: "grey.500",
              "&:hover": { color: "error.main" },
            }}
            variant="text"
          >
            <MdsIcon fontSize="small" icon={MdsChips} />
          </Button>
          {field === last(fields) && <AddTimeButton onClick={() => append(timeRange.getDefault())} />}
        </Stack>
      ))}
      {error &&
        errorMessage.map((label) => (
          <FormHelperText key={label} error>
            {label}
          </FormHelperText>
        ))}
    </Stack>
  );
};

const AddTimeButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    color="success"
    onClick={onClick}
    size="medium"
    sx={{ minWidth: "auto", maxHeight: "32px", px: 0 }}
    variant="text"
  >
    <MdsIcon fontSize="small" icon={MdsAddBox} />
  </Button>
);

const TimeRangeItem = ({ index }: { index: number }) => {
  const { trigger } = useFormContext();
  const {
    field: { errorMessage: fromErrMsg, error: fromErr, ...fromField },
  } = useMuiController<FieldValues, string>({ name: `times.${index}.from` });
  const {
    field: { errorMessage: toErrMsg, error: toErr, ...toField },
  } = useMuiController<FieldValues, string>({ name: `times.${index}.to` });

  const handleFromChange = useCallback(
    (val: string) => {
      fromField.onChange(val);
      trigger();
    },
    [fromField, trigger]
  );

  const handleToChange = useCallback(
    (val: string) => {
      toField.onChange(val);
      trigger();
    },
    [toField, trigger]
  );

  return (
    <FormControl error={fromErr || toErr}>
      <Stack alignItems="center" direction="row" spacing={0.5} sx={{ maxWidth: "200px" }}>
        <StringTimeInput
          {...fromField}
          error={fromErr || toErr}
          FormControlProps={{ sx: { maxWidth: "calc(50% - 8px)" } }}
          fullWidth={false}
          onChange={handleFromChange}
          size="small"
          sx={{ "&>input.MuiInputBase-input": { textAlign: "center", p: 0 } }}
        />
        <Box sx={{ width: "8px", textAlign: "center" }}>-</Box>
        <StringTimeInput
          {...toField}
          error={fromErr || toErr}
          FormControlProps={{ sx: { maxWidth: "calc(50% - 8px)" } }}
          fullWidth={false}
          includeTime="end"
          onChange={handleToChange}
          size="small"
          sx={{ "&>input.MuiInputBase-input": { textAlign: "center", p: 0 } }}
        />
      </Stack>
      {uniq([...fromErrMsg, ...toErrMsg]).map((label) => (
        <FormHelperText key={label}>{label}</FormHelperText>
      ))}
    </FormControl>
  );
};
